import { pdfAPIService } from './pdf-api-service';
import { bidAttachmentService } from './bid-attachment-service';
import { bidApiService } from './bid-api-service';
import { compareBidDocuments } from './bid-ai-api';
import type { AiBidComparison, BidComparisonInput } from '../types';

const formatMoney = (value: number) => `$${value.toLocaleString('en-US')}`;

/** A saved bid as plain text for the AI: its fields and line items (used when it has no PDF to read). */
function describeSavedBid(bid: any): string {
  const total = bid.amount ?? bid.total_amount;
  const lines = [
    `Subcontractor: ${bid.subcontractor_name}`,
    `Date received: ${bid.date_received}`,
    bid.category ? `Category: ${bid.category}` : '',
    `Total: ${total ? formatMoney(total) : 'not specified'}`,
    bid.description ? `Description: ${bid.description}` : '',
    bid.payment_terms ? `Payment terms: ${bid.payment_terms}` : '',
  ];
  (bid.bid_items ?? []).forEach((item: any, index: number) => {
    const parts = [`${index + 1}. ${item.description || 'Item'}`];
    if (item.quantity) parts.push(`qty ${item.quantity}${item.unit ? ` ${item.unit}` : ''}`);
    if (item.unit_price) parts.push(`unit price ${formatMoney(item.unit_price)}`);
    if (item.total_price) parts.push(`total ${formatMoney(item.total_price)}`);
    lines.push(parts.join(' | '));
  });
  return lines.filter(Boolean).join('\n');
}

/** Bids saved in KrewUp -> the documents to compare: the bid's PDF when it has one, otherwise its own data. */
async function toComparisonInput(bidId: string): Promise<BidComparisonInput | null> {
  const bid = await bidApiService.getBid(bidId);
  if (!bid) return null;

  let text = '';
  try {
    const pdfPath = await bidAttachmentService.getBidPdfStoragePath(bidId);
    if (pdfPath) text = (await pdfAPIService.parsePDFFromStorage(pdfPath)).text;
  } catch (error) {
    console.error(`[Bid comparison] the PDF of bid ${bidId} could not be read, using its saved data instead`, error);
  }
  return {
    nameHint: bid.subcontractor_name,
    text: text || describeSavedBid(bid),
    knownTotal: bid.amount ?? bid.total_amount ?? null,
  };
}

export const bidComparisonService = {
  /** Compares bids already saved in KrewUp (the "Compare Selected" action of the Overview table). */
  async compareSavedBids(bidIds: string[]): Promise<AiBidComparison> {
    const inputs = (await Promise.all(bidIds.map(toComparisonInput))).filter((input): input is BidComparisonInput => !!input);
    if (inputs.length < 2) throw new Error('At least 2 of the selected bids could be loaded. Please try again.');
    return compareBidDocuments(inputs);
  },
};
